import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useSearch } from 'wouter';
/**
 * URL <-> worklist view-state for the Study list page.
 *
 * The Study list keeps *all* of its view state — search boxes, dropdown
 * filters, sort, and pagination — in the URL query string so that a refresh
 * restores the exact view and a copied URL reproduces it for someone else.
 *
 * Defaults are deliberately *omitted* from the URL (page=1, the default sort,
 * an empty search, the "all" status pill, …) so a pristine worklist is just
 * `/study/list` and a shared URL only carries what the user actually changed.
 */
const WORKLIST_DEFAULTS = {
    page: 1,
    pageSize: 25,
    sortBy: 'StudyDate',
    sortDirection: 'Desc',
    status: 'all',
};
// The status pill's known values. An unknown value from a hand-edited URL
// must fall back to the default, otherwise the <Select> renders an empty
// selection that matches no <SelectItem>.
const WORKLIST_STATUSES = ['all', 'unread', 'stat'];
// Filter keys whose value is a plain string — these round-trip 1:1 with a
// query param of the same name. `reportSection`, the numeric/boolean fields,
// sort, and pagination are handled explicitly below because they need
// validation or default-omission.
const STRING_PARAM_KEYS = [
    'search',
    'referringPhysiciansName',
    'reportKeyword',
    'tag',
    'patientName',
    'ownerName',
    'species',
    'description',
    'fromDateTime',
    'toDateTime',
    'modality',
    'accessionNumber',
    'clinicCode',
    'institutionName',
    'folderId',
];
function isReportSection(value) {
    return value === 'all' || value === 'impression' || value === 'findings';
}
/** Parse a URL search string into the worklist query (defaults applied). */
export function parseWorklistQuery(search) {
    const params = new URLSearchParams(search);
    const filters = {};
    for (const key of STRING_PARAM_KEYS) {
        const value = params.get(key);
        if (value)
            filters[key] = value;
    }
    const reportSection = params.get('reportSection');
    if (isReportSection(reportSection))
        filters.reportSection = reportSection;
    if (params.get('favouritesOnly') === 'true')
        filters.favouritesOnly = true;
    // `sortBy` is validated loosely: the table only emits known columns and the
    // backend rejects anything unknown, so an out-of-range value is harmless.
    const sortBy = params.get('sortBy');
    filters.sortBy = sortBy || WORKLIST_DEFAULTS.sortBy;
    filters.sortDirection =
        params.get('sortDirection') === 'Asc' ? 'Asc' : WORKLIST_DEFAULTS.sortDirection;
    const page = Number(params.get('page'));
    filters.page = Number.isInteger(page) && page >= 1 ? page : WORKLIST_DEFAULTS.page;
    const pageSize = Number(params.get('pageSize'));
    filters.pageSize =
        Number.isInteger(pageSize) && pageSize >= 1 ? pageSize : WORKLIST_DEFAULTS.pageSize;
    return filters;
}
/** Read the status pill ("all" | "unread" | "stat") from a URL search string. */
export function parseWorklistStatus(search) {
    const status = new URLSearchParams(search).get('status');
    return WORKLIST_STATUSES.includes(status)
        ? status
        : WORKLIST_DEFAULTS.status;
}
/** Serialize worklist state back to a URL search string, omitting defaults. */
export function serializeWorklistState(filters, status) {
    const params = new URLSearchParams();
    for (const key of STRING_PARAM_KEYS) {
        const value = filters[key];
        if (value)
            params.set(key, String(value));
    }
    if (filters.reportSection && filters.reportSection !== 'all') {
        params.set('reportSection', filters.reportSection);
    }
    if (filters.favouritesOnly)
        params.set('favouritesOnly', 'true');
    if (filters.sortBy && filters.sortBy !== WORKLIST_DEFAULTS.sortBy) {
        params.set('sortBy', filters.sortBy);
    }
    if (filters.sortDirection && filters.sortDirection !== WORKLIST_DEFAULTS.sortDirection) {
        params.set('sortDirection', filters.sortDirection);
    }
    if (filters.page && filters.page !== WORKLIST_DEFAULTS.page) {
        params.set('page', String(filters.page));
    }
    if (filters.pageSize && filters.pageSize !== WORKLIST_DEFAULTS.pageSize) {
        params.set('pageSize', String(filters.pageSize));
    }
    if (status && status !== WORKLIST_DEFAULTS.status)
        params.set('status', status);
    // Deterministic param order keeps shared URLs stable and tests predictable.
    params.sort();
    return params.toString();
}
/**
 * Single source of truth for Study-list view state, backed by the URL.
 *
 * `filters`/`status` are derived from the URL; the setters serialize back and
 * navigate with history *replace* so that typing in a search box doesn't
 * flood the back-stack. A ref mirrors the latest applied state so that two
 * setter calls in the same event (e.g. "clear all" touching both slices)
 * compose against fresh values instead of a stale render snapshot.
 */
export function useWorklistUrlState() {
    const search = useSearch();
    const [pathname, navigate] = useLocation();
    const filters = useMemo(() => parseWorklistQuery(search), [search]);
    const status = useMemo(() => parseWorklistStatus(search), [search]);
    // Mirror the latest URL-derived state so that two setter calls in the same
    // event compose against fresh values. Synced after each commit (the ref is
    // current before any user event fires) and updated synchronously inside the
    // navigation primitive below.
    const stateRef = useRef({ filters, status });
    useEffect(() => {
        stateRef.current = { filters, status };
    }, [filters, status]);
    const setWorklistState = useCallback((nextFilters, nextStatus) => {
        stateRef.current = { filters: nextFilters, status: nextStatus };
        const qs = serializeWorklistState(nextFilters, nextStatus);
        navigate(qs ? `${pathname}?${qs}` : pathname, { replace: true });
    }, [navigate, pathname]);
    const setFilters = useCallback((next) => setWorklistState(next, stateRef.current.status), [setWorklistState]);
    const setStatus = useCallback((next) => setWorklistState({ ...stateRef.current.filters, page: 1 }, next), [setWorklistState]);
    return { filters, status, setFilters, setStatus };
}
